import { PieceDto, TaskDto } from "@growth/dto";
import { Badge, Button, Field, Input, Textarea } from "@growth/ui";
import {
  ArrowLeft,
  Check,
  CircleAlert,
  LoaderCircle,
  RotateCcw,
  Save,
  Send,
  X,
} from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ErrorText } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { useEditPiece, usePiece, useTaskAction } from "../lib/content";
import { formatRelative } from "../lib/format";
import { kindLabel, StatusBadge } from "./content";
import { Slides } from "./piece-slides";
import { Versions } from "./piece-versions";

const Card = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="space-y-2 rounded-xl border border-line bg-white p-4">
    <h3 className="text-xs font-semibold uppercase tracking-widest text-muted">{title}</h3>
    {children}
  </div>
);

const List = ({ items, tone }: { items: string[]; tone?: "problem" | "warning" }) => (
  <ul className="space-y-1 text-sm">
    {items.map((item) => (
      <li
        key={item}
        className={
          tone === "problem" ? "text-red-700" : tone === "warning" ? "text-amber-700" : "text-ink"
        }
      >
        {item}
      </li>
    ))}
  </ul>
);

/** One piece: what the agents wrote, what the checks said, and what a person needs to do. */
export const PiecePage = () => {
  const { id = "" } = useParams();
  const piece = usePiece(id);

  if (piece.isPending)
    return <LoaderCircle className="m-8 size-6 animate-spin text-muted" aria-label="Loading" />;
  if (piece.isError) return <ErrorText>{apiErrorMessage(piece.error)}</ErrorText>;
  const p = piece.data;

  return (
    <section className="mx-auto max-w-6xl space-y-6">
      <Link
        to="/content"
        className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Content
      </Link>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <p className="font-mono text-xs text-muted">
            {p.code} · {kindLabel(p)} · {p.pillar}
            {p.persona && ` · ${p.persona}`}
          </p>
          <h1 className="font-serif text-2xl text-ink">{p.idea}</h1>
        </div>
        <StatusBadge status={p.status} />
      </header>
      <Versions piece={p} />

      {p.status === "drafting" ? (
        <div className="flex items-center gap-3 rounded-xl border border-line bg-white p-6 text-sm text-muted">
          <LoaderCircle className="size-5 animate-spin" />
          {p.format === "carousel"
            ? "The designer is laying out slides; the claims check and the art director (who looks at the rendered slides) review each version. This takes a few minutes."
            : "The writer is drafting; the claims check and the critic review each version. This takes a minute or two."}
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="min-w-0 space-y-4">
            {p.tasks.map((task) => (
              <TaskPanel key={task.id} task={task} piece={p} />
            ))}
            {/* A task already explains the error; show it here only when nothing else does. */}
            {p.error && p.tasks.length === 0 && <ErrorText>{p.error}</ErrorText>}
            {p.carousel && <Slides piece={p} carousel={p.carousel} />}
            {(p.body || p.status !== "failed") && <Draft piece={p} />}
          </div>
          <aside className="space-y-4">
            <Checks piece={p} />
            {(p.status === "approved" || p.status === "posted") && (
              <Card title="Posting steps">
                <ol className="list-decimal space-y-1 pl-4 text-sm">
                  {p.postingSteps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
                {p.postedUrl && (
                  <a
                    href={p.postedUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-accent underline"
                  >
                    Live post
                  </a>
                )}
              </Card>
            )}
            <p className="text-xs text-muted">
              {p.rounds} round{p.rounds === 1 ? "" : "s"} · about US${p.costUsd.toFixed(3)} ·
              updated {formatRelative(p.updatedAt)}
            </p>
          </aside>
        </div>
      )}
    </section>
  );
};

const Draft = ({ piece }: { piece: PieceDto }) => {
  const { atLeast } = useActive();
  const editable =
    atLeast("EDITOR") &&
    ["awaiting_approval", "needs_human", "blocked", "failed"].includes(piece.status);
  const edit = useEditPiece(piece.id);
  const [body, setBody] = useState(piece.body);
  const [firstComment, setFirstComment] = useState(piece.firstComment ?? "");
  const dirty = body !== piece.body || firstComment !== (piece.firstComment ?? "");

  const save = (event: FormEvent) => {
    event.preventDefault();
    edit.mutate({ body, firstComment: firstComment.trim() ? firstComment : null });
  };

  return (
    <form onSubmit={save} className="space-y-3 rounded-xl border border-line bg-white p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-lg text-ink">
          {piece.channel === "instagram"
            ? "The caption"
            : piece.format === "carousel"
              ? "The post text"
              : "The post"}
        </h2>
        {piece.edited && <Badge>Edited by a person</Badge>}
      </div>
      <Textarea
        aria-label="Post text"
        value={body}
        readOnly={!editable}
        onChange={(event) => setBody(event.target.value)}
        rows={14}
        className="text-[15px] leading-relaxed"
      />
      {(piece.channel === "linkedin" || piece.firstComment) && (
        <Field label="First comment (carries the tracked link)">
          {(props) => (
            <Input
              {...props}
              readOnly={!editable}
              value={firstComment}
              onChange={(event) => setFirstComment(event.target.value)}
            />
          )}
        </Field>
      )}
      {piece.placeholders.length > 0 && (
        <p className="text-sm text-amber-700">
          To fill before approval: {piece.placeholders.join("; ")}
        </p>
      )}
      {edit.isError && <ErrorText>{apiErrorMessage(edit.error)}</ErrorText>}
      {editable && (
        <Button type="submit" variant="secondary" disabled={!dirty || edit.isPending}>
          {edit.isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Save className="size-4" />
          )}
          Save edit (re-runs the claims check)
        </Button>
      )}
    </form>
  );
};

const Checks = ({ piece }: { piece: PieceDto }) => (
  <>
    {piece.review && (
      <Card title={piece.format === "carousel" ? "Art director" : "Critic"}>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
          {Object.entries(piece.review.scores).map(([key, value]) => (
            <div key={key} className="flex justify-between">
              <dt className="text-muted">{key}</dt>
              <dd className={value >= 4 ? "font-medium" : "font-medium text-red-700"}>{value}/5</dd>
            </div>
          ))}
        </dl>
        <p className="text-sm italic text-muted">{piece.review.verdict}</p>
        {piece.review.mustFix.length > 0 && <List items={piece.review.mustFix} tone="problem" />}
        {piece.review.niceToHave.length > 0 && <List items={piece.review.niceToHave} />}
      </Card>
    )}
    {piece.claimsCheck && (
      <Card title="Claims check">
        {piece.claimsCheck.problems.length === 0 && piece.claimsCheck.warnings.length === 0 ? (
          <p className="flex items-center gap-1.5 text-sm text-emerald-700">
            <Check className="size-4" /> Nothing outside the claims register
          </p>
        ) : (
          <>
            <List items={piece.claimsCheck.problems} tone="problem" />
            <List items={piece.claimsCheck.warnings} tone="warning" />
          </>
        )}
      </Card>
    )}
    {piece.editCheck && (
      <Card title={`Check of your edit (${formatRelative(piece.editCheck.at)})`}>
        {piece.editCheck.layoutProblems && piece.editCheck.layoutProblems.length > 0 && (
          <List items={piece.editCheck.layoutProblems} tone="warning" />
        )}
        {piece.editCheck.problems.length === 0 ? (
          <p className="text-sm text-emerald-700">Your edit passes the claims check.</p>
        ) : (
          <>
            <List items={piece.editCheck.problems} tone="problem" />
            <p className="text-xs text-muted">
              You can still approve: the override is kept on record.
            </p>
          </>
        )}
      </Card>
    )}
    {piece.notesForHuman && (
      <Card title={piece.format === "carousel" ? "Designer's note" : "Writer's note"}>
        <p className="text-sm">{piece.notesForHuman}</p>
      </Card>
    )}
  </>
);

/** What a person can do next with this piece. */
const TaskPanel = ({ task, piece }: { task: TaskDto; piece: PieceDto }) => {
  const { atLeast } = useActive();
  const act = useTaskAction();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [url, setUrl] = useState("");
  const canReview = atLeast("REVIEWER");
  const canEdit = atLeast("EDITOR");

  if (task.busy) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm">
        <LoaderCircle className="size-4 animate-spin" /> Working on it…
      </div>
    );
  }

  const reject = (redraft: boolean) =>
    act.mutate(
      { kind: "reject", taskId: task.id, input: { reason, redraft } },
      { onSuccess: () => setRejecting(false) },
    );

  return (
    <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
      <p className="flex items-start gap-2 font-medium text-ink">
        <CircleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
        {task.title}
      </p>
      <p className="whitespace-pre-line text-sm text-muted">{task.detail}</p>

      {task.type === "post_manually" && canEdit && (
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            act.mutate({ kind: "posted", taskId: task.id, url });
          }}
        >
          <Input
            aria-label="Live post URL"
            type="url"
            required
            placeholder="https://…"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            className="min-w-64 flex-1"
          />
          <Button type="submit" disabled={act.isPending}>
            <Check className="size-4" /> Mark posted
          </Button>
        </form>
      )}

      {task.type !== "post_manually" && (
        <div className="flex flex-wrap gap-2">
          {task.type === "approve" && canReview && (
            <Button
              onClick={() => act.mutate({ kind: "approve", taskId: task.id })}
              disabled={
                act.isPending || piece.placeholders.length > 0 || Boolean(piece.carousel?.rendering)
              }
            >
              <Check className="size-4" /> Approve
            </Button>
          )}
          {(task.type === "decide" || task.type === "unblock") &&
            canEdit &&
            piece.status !== "failed" && (
              <Button
                onClick={() => act.mutate({ kind: "send", taskId: task.id })}
                disabled={act.isPending}
              >
                <Send className="size-4" /> It's good: send for approval
              </Button>
            )}
          {piece.status === "failed" && canReview && (
            <Button
              onClick={() =>
                act.mutate({
                  kind: "reject",
                  taskId: task.id,
                  input: { reason: "The last draft failed to run; try again", redraft: true },
                })
              }
              disabled={act.isPending}
            >
              <RotateCcw className="size-4" /> Try again
            </Button>
          )}
          {canReview && (
            <Button
              variant="secondary"
              onClick={() => setRejecting((open) => !open)}
              disabled={act.isPending}
            >
              <X className="size-4" /> Reject…
            </Button>
          )}
        </div>
      )}

      {rejecting && (
        <div className="space-y-2">
          <Textarea
            aria-label="Why"
            placeholder="Why? The writer reads this if you ask for a redraft."
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              disabled={reason.trim().length < 3 || act.isPending}
              onClick={() => reject(true)}
            >
              <RotateCcw className="size-4" /> Reject and redraft
            </Button>
            <Button
              variant="secondary"
              disabled={reason.trim().length < 3 || act.isPending}
              onClick={() => reject(false)}
            >
              Reject for good
            </Button>
          </div>
        </div>
      )}
      {act.isError && <ErrorText>{apiErrorMessage(act.error)}</ErrorText>}
    </div>
  );
};
